import { useState } from 'react';
import { Alert, Box, Button, FormField, Input, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import {
  PRIORITY_BG,
  PRIORITY_BORDER,
  PRIORITY_LEGEND_BG,
  type PriorityTier,
} from '../../../utils/priority';
import { labelToPriority, type SrOLabel } from '../utils/priorityCycle';

interface NewUcModalProps {
  industry: string;
  solutionArea: string;
  saving: boolean;
  onClose: () => void;
  /** Resolves `null` on success, or a ready-to-show failure message (already correct
   *  about retry-ability — a 403 must not be reported as retryable). */
  onSave: (input: { useCase: string; priority: number }) => Promise<string | null>;
}

const OPTIONS: { value: SrOLabel; tier: PriorityTier; label: string }[] = [
  { value: 'S', tier: 'strategic', label: 'Strategic' },
  { value: 'R', tier: 'recommended', label: 'Recommended' },
  { value: 'O', tier: 'opportunistic', label: 'Opportunistic' },
];

export default function NewUcModal({
  industry,
  solutionArea,
  saving,
  onClose,
  onSave,
}: NewUcModalProps) {
  const [useCase, setUseCase] = useState('');
  const [priority, setPriority] = useState<SrOLabel>('S');
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setError(null);
    const name = useCase.trim();
    if (!name) {
      setError('UC 名称不能为空');
      return;
    }
    const failure = await onSave({ useCase: name, priority: labelToPriority(priority) });
    if (failure) setError(failure);
  };

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!saving) onClose();
      }}
      header={`新建 UC（${industry} / ${solutionArea}）`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={!useCase.trim()}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="small">在该 Solution Area 列末追加一个 UC</Box>

        <FormField label="Use Case Name *">
          <Input
            value={useCase}
            onChange={({ detail }) => setUseCase(detail.value)}
            disabled={saving}
            autoFocus
            placeholder="e.g. Anti-Cheat"
          />
        </FormField>

        <FormField label="Priority *">
          <div className="ivt-priority-radio-group" role="radiogroup" aria-label="优先级">
            {OPTIONS.map((opt) => {
              const selected = priority === opt.value;
              return (
                <label
                  key={opt.value}
                  className={
                    'ivt-priority-radio' + (selected ? ' ivt-priority-radio--selected' : '')
                  }
                  style={{
                    background: PRIORITY_BG[opt.tier],
                    borderColor: selected
                      ? PRIORITY_LEGEND_BG[opt.tier]
                      : PRIORITY_BORDER[opt.tier],
                  }}
                >
                  <input
                    type="radio"
                    name="newuc-priority"
                    value={opt.value}
                    checked={selected}
                    disabled={saving}
                    onChange={() => setPriority(opt.value)}
                  />
                  <span
                    className="ivt-priority-radio__chip"
                    style={{ background: PRIORITY_LEGEND_BG[opt.tier] }}
                  >
                    {opt.value}
                  </span>
                  <span className="ivt-priority-radio__label">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </FormField>

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
